import { memo, useMemo, useCallback, useState } from 'react';
import { Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogoutOutlined, ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';

// Components
import SidebarMenuItem from './components/SidebarMenuItem';
import SidebarSubmenu from './components/SidebarSubmenu';

// Hooks
import { useSidebar } from './hooks/useSidebar';
import { useSidebarNavigation } from './hooks/useSidebarNavigation';
import { useRoleManager } from '@/shared/services/useRoleManager';
import { useAppSelector, useAppDispatch } from '@/store/storeHooks';
import { logout } from '@/store/reducers/LoginSlice';

// Constants and types
import { SIDEBAR_DIMENSIONS, SIDEBAR_ANIMATION } from './constants/sidebar.constants';
import { sidebarRoutes, sidebarTeacherRoutes } from './routes/sidebarRoutes';
import type { ISidebarRoute } from './types/sidebar.types';

/**
 * Main Sidebar component
 * Renders the application sidebar with navigation menu
 */
const Sidebar = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();

  // Custom hooks
  const sidebar = useSidebar();
  const navigation = useSidebarNavigation();
  const hasAccess = useRoleManager();
  const dispatch = useAppDispatch();

  // Get user data from Redux
  const loginData = useAppSelector((state) => state.LoginState.loginData);
  const userType = loginData?.type;

  // Logout confirmation modal state
  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  // Handle logout button click - open confirmation modal
  const handleLogoutClick = useCallback(() => {
    setOpenLogoutModal(true);
  }, []);

  // Handle logout confirmation
  const handleLogout = useCallback(() => {
    localStorage.clear();
    dispatch(logout());
    navigate('/login');
  }, [dispatch, navigate]);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setOpenLogoutModal(false);
  }, []);

  // Filter routes based on user type and permissions
  const availableRoutes = useMemo(() => {
    const routes = userType === 'admin' ? sidebarRoutes : [];

    const filteredRoutes = routes.filter((route) => {
      if (route.role && !hasAccess(route.role as any)) {
        return false;
      }

      if (route.items?.length) {
        route.items = route.items.filter((item) => {
          const hasItemAccess = !item.role || hasAccess(item.role as any);
          return hasItemAccess;
        });
      }

      return true;
    });

    return filteredRoutes;
  }, [userType, hasAccess, loginData]);


  /**
   * Check if a menu item is active based on current location
   */
  const isMenuItemActive = useCallback((item: ISidebarRoute): boolean => {
    if (!item.path) return false;

    // Check if current path matches the item path
    if (location.pathname === item.path) {
      return true;
    }

    // Check if current path starts with the item path (for nested routes)
    if (location.pathname.startsWith(item.path + '/')) {
      return true;
    }

    // Check if any submenu items are active
    if (item.items?.length) {
      return item.items.some(subItem => {
        if (!subItem.path) return false;
        return location.pathname === subItem.path ||
          location.pathname.startsWith(subItem.path + '/');
      });
    }

    return false;
  }, [location.pathname]);

  /**
   * Render menu item based on type
   */
  const renderMenuItem = useCallback((item: ISidebarRoute, index: number) => {
    const isActive = isMenuItemActive(item);

    // If item has children, render as submenu
    if (item.items?.length) {
      return (
        <SidebarSubmenu
          key={item.id || index}
          parentItem={item}
          isExpanded={sidebar.state.isExpanded}
          onToggle={() => { }} // Handle submenu toggle if needed
          onItemClick={(item) => {
            const mockEvent = {
              stopPropagation: () => { },
              preventDefault: () => { },
            } as React.MouseEvent;
            navigation.handleSubmenuItemClick(item, mockEvent);
          }}
        />
      );
    }

    // Render as regular menu item
    return (
      <SidebarMenuItem
        key={item.id || index}
        item={item}
        isExpanded={sidebar.state.isExpanded}
        isActive={isActive}
        onClick={navigation.handleMenuItemClick}
        onMouseEnter={() => navigation.handleMenuItemHover(item)}
      />
    );
  }, [isMenuItemActive, sidebar.state.isExpanded, navigation]);

  const isExpanded = sidebar.state.isExpanded;
  const sidebarWidth = isExpanded ? SIDEBAR_DIMENSIONS.EXPANDED_WIDTH : SIDEBAR_DIMENSIONS.COLLAPSED_WIDTH;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        width: sidebarWidth,
        transition: `width ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        borderRight: '1px solid #E2E8F0',
        zIndex: 99,
        overflow: 'hidden',
        boxShadow: '4px 0 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — moslashuvchan user blok */}
      <Box
        sx={{
          flexShrink: 0,
          padding: isExpanded ? '12px 10px' : '12px 8px',
          borderBottom: '1px solid #E2E8F0',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            gap: isExpanded ? 10 : 0,
            padding: isExpanded ? '10px 12px' : '10px',
            minHeight: 44,
            background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
            borderRadius: isExpanded ? '14px' : '12px',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.25)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflow: 'hidden',
            transition: `padding ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms ease, border-radius ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms ease, gap ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms ease`,
          }}
        >
          {/* Avatar — kichrayishdan saqlanadi */}
          <Box
            sx={{
              width: isExpanded ? 40 : 36,
              height: isExpanded ? 40 : 36,
              minWidth: isExpanded ? 40 : 36,
              flexShrink: 0,
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: isExpanded ? '12px' : '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: isExpanded ? '1.125rem' : '1rem',
              fontWeight: 700,
              backdropFilter: 'blur(10px)',
              transition: `width ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms ease, height ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms ease, font-size ${SIDEBAR_ANIMATION.TRANSITION_DURATION}ms ease`,
            }}
          >
            {loginData?.full_name?.charAt(0)?.toUpperCase() || 'V'}
          </Box>
          {/* Ism va telefon — flex orqali joy egallaydi, tor joyda ellipsis */}
          {isExpanded && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                justifyContent: 'start',
                overflow: 'hidden',
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 500,
                  color: '#FFFFFF',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                }}
              >
                {loginData?.full_name || 'Vatan'}
              </Box>
              {loginData?.phone_number ? (
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: { xs: '0.65rem', sm: '0.6875rem' },
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.85)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2,
                  }}
                >
                  {loginData.phone_number}
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>

      {/* Main Menu */}
      <Box sx={{
        flex: 1,
        padding: isExpanded ? '8px 10px' : '12px 10px',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: isExpanded ? 2 : 0,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#CBD5E1',
          borderRadius: '2px',
          '&:hover': {
            backgroundColor: '#94A3B8',
          },
        },
      }}>
        {availableRoutes.map((route, index) => renderMenuItem(route, index))}
      </Box>

      {/* Footer: tugmalar orasidagi joy minimal */}
      <Box sx={{
        flexShrink: 0,
        padding: isExpanded ? '10px 14px' : '10px',
        borderTop: '1px solid #E2E8F0',
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        display: 'flex',
        alignItems: 'center',
        // justifyContent: 'space-between',
        justifyContent: isExpanded ? 'space-between' : 'center',
        flexDirection: isExpanded ? 'row' : 'column',
        gap: 4,
      }}>
        <Tooltip title="Chiqish" arrow placement="right">
          <IconButton
            onClick={handleLogoutClick}
            sx={{
              color: '#64748B',
              padding: '10px',
              borderRadius: '12px',
              backgroundColor: '#F1F5F9',
              minWidth: 40,
              minHeight: 40,
              '&:hover': {
                color: '#EF4444',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <LogoutOutlined sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={isExpanded ? 'Sidebarni yopish' : 'Sidebarni ochish'} arrow placement="right">
          <IconButton
            onClick={sidebar.toggleExpansion}
            sx={{
              color: isExpanded ? '#64748B' : '#3B82F6',
              padding: '10px',
              borderRadius: '12px',
              backgroundColor: isExpanded ? '#F1F5F9' : 'rgba(59, 130, 246, 0.12)',
              minWidth: 40,
              minHeight: 40,
              '&:hover': {
                color: '#3B82F6',
                backgroundColor: '#E0E7FF',
              },
            }}
          >
            {isExpanded ? (
              <ChevronLeftRounded sx={{ fontSize: '22px' }} />
            ) : (
              <ChevronRightRounded sx={{ fontSize: '22px' }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Logout Confirmation Modal */}
      <Dialog
        open={openLogoutModal}
        onClose={handleCloseModal}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
            minWidth: '400px',
          }
        }}
      >
        <DialogTitle id="logout-dialog-title" sx={{
          fontSize: '20px',
          fontWeight: 600,
          padding: '20px 24px 16px',
        }}>
          Chiqishni tasdiqlash
        </DialogTitle>
        <DialogContent sx={{ padding: '0 24px' }}>
          <DialogContentText id="logout-dialog-description" sx={{
            fontSize: '16px',
            color: '#64748B',
          }}>
            Rostan ham chiqishni xohlaysizmi?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{
          padding: '16px 24px 20px',
          gap: '12px',
        }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              padding: '8px 20px',
              borderColor: '#E2E8F0',
              color: '#64748B',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Bekor qilish
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            color="error"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              padding: '8px 20px',
              backgroundColor: '#EF4444',
              '&:hover': {
                backgroundColor: '#DC2626',
              },
            }}
          >
            Chiqish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;